import { getUserInfo } from "./AuthUtils";
import { PRODUCTS, PRODUCT_ORDER } from "../constants/permissions";

/**
 * The portal's `info` payload is inconsistent: `applications` may be an ARRAY of
 * app entries, or a SINGLE app OBJECT when the user has just one. Normalize to an
 * array so callers can treat both the same way.
 */
const toApplicationsArray = (applications) => {
  if (Array.isArray(applications)) return applications;
  if (applications && typeof applications === "object") return [applications];
  return [];
};

/**
 * Check whether the user holds `action` on `resource` within a given application.
 * Mirrors the auth portal's `info` shape:
 *   applications[].permissions[resource] = [action, ...]
 *
 * @param {Object} userDetails    decoded info payload (falls back to stored userInfo)
 * @param {string} applicationName e.g. "Geo.Console"
 * @param {string} resource        e.g. "integration-geo"
 * @param {string} action          e.g. "view"
 */
export const checkPermission = (
  userDetails,
  applicationName,
  resource,
  action
) => {
  const user = userDetails || getUserInfo();
  if (!user || !applicationName || !resource || !action) {
    return false;
  }

  const applications = toApplicationsArray(user?.applications);
  const app = applications.find(
    (item) => item?.applicationName === applicationName
  );

  const resourcePerms = app?.permissions?.[resource];
  return Array.isArray(resourcePerms) && resourcePerms.includes(action);
};

/**
 * Whether the user can view a given integration product ("geo" | "si" | "kyc").
 */
export const hasProductAccess = (productKey, userDetails) => {
  const product = PRODUCTS[productKey];
  if (!product) return false;
  return checkPermission(
    userDetails,
    product.applicationName,
    product.resource,
    product.action
  );
};

/**
 * List of product keys the user can view, in canonical PRODUCT_ORDER.
 */
export const getAccessibleProducts = (userDetails) => {
  const user = userDetails || getUserInfo();
  return PRODUCT_ORDER.filter((key) => hasProductAccess(key, user));
};

/**
 * How many applications (site permissions) the user holds. Note that a user with
 * a single application arrives as an OBJECT rather than a one-element array, so
 * this must go through the normalizer to count correctly.
 */
export const getApplicationCount = (userDetails) => {
  const user = userDetails || getUserInfo();
  return toApplicationsArray(user?.applications).length;
};

/**
 * Whether the user can return to the auth portal's console picker. With only one
 * application there is nothing to pick between, and the portal bounces them back
 * out (logging them out in the process) — so the ACT logo must not be clickable.
 */
export const canReturnToPortal = (userDetails) =>
  getApplicationCount(userDetails) > 1;

/**
 * Whether the user has at least one jurisdiction in ANY application entry.
 * Jurisdictions are attached per application (e.g. Geo.Console.jurisdictions);
 * an empty/absent list means the user has no assigned jurisdictions.
 */
export const hasJurisdictions = (userDetails) => {
  const user = userDetails || getUserInfo();
  const applications = toApplicationsArray(user?.applications);
  return applications.some(
    (app) => Array.isArray(app?.jurisdictions) && app.jurisdictions.length > 0
  );
};
